/* ============================================================================
   EVMG · Arrival choreography — motion layer
   Restrained, taste-critical: motion you feel more than see.

   Everything here is gated so that under prefers-reduced-motion OR no-JS the
   page renders fully visible and static. The CSS hidden-initial states only
   apply under `html.js` + `@media (prefers-reduced-motion: no-preference)`;
   this module only *reveals* things, so even if it never ran, the reduced-motion
   / no-JS paths keep content visible.
   ============================================================================ */

(() => {
  const prefersReduced = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  // Under reduced motion, do nothing. CSS never hid anything in that mode,
  // so there is nothing to reveal — content is already visible and static.
  if (prefersReduced) return;

  /* ---- 0) Brand-reveal intro · FLIP the lockup onto the header logo ------ */
  /* The CSS phases (draw → letters → re-center → underscore → hold) run purely
     in CSS. This module owns only the FINAL move: at ~1900ms it FLIPs the whole
     lockup onto the real header logo while fading the dark veil, hands off to the
     page by firing `is-ready`, then clears the veil.

     Adding `intro--js` here (as early as possible) SUPPRESSES the CSS
     intro-safety-lift, so the no-JS fallback (which would otherwise clear the
     veil at ~2600ms) never competes with the JS move. Everything is guarded:
     if the header target is missing or anything throws, we fall back to a plain
     veil fade + clear and still fire is-ready — the veil can never trap the user. */
  (() => {
    const intro = document.querySelector('.intro');
    // Intro is display:none under intro-skip and reduced-motion. Detect that via
    // computed display (offsetParent is unreliable for position:fixed elements).
    // When hidden it isn't playing — leave the reveal to the head-gate.
    if (!intro || getComputedStyle(intro).display === 'none') return;

    // Claim the intro for JS: cancels the CSS safety-lift, we drive the move.
    intro.classList.add('intro--js');

    const FLIP_START_MS = 2050; // when the lockup begins flying to the header (after compose + hold)
    const FLIP_MS = 820;        // duration of the fly-to-header move
    const FADE_MS = 640;        // veil background fade
    const EASE = 'cubic-bezier(.2,0,0,1)';
    // Gentle ease-in-out for the move itself — eases away from centre and settles
    // softly onto the header, which reads calmer than a sharp deceleration.
    const MOVE_EASE = 'cubic-bezier(.5,0,.15,1)';

    const d = document.documentElement;
    const fireReady = () => d.classList.add('is-ready');

    const clearIntro = () => {
      intro.style.transition = 'opacity 260ms ease';
      intro.style.opacity = '0';
      intro.style.pointerEvents = 'none';
      const done = () => {
        intro.style.visibility = 'hidden';
        intro.style.display = 'none';
      };
      // Belt-and-suspenders: clear even if transitionend never fires.
      intro.addEventListener('transitionend', done, { once: true });
      setTimeout(done, 400);
    };

    const stage = intro.querySelector('.intro__stage');
    // Measure the lockup (a div — reliable rect, unlike the raw <svg>). Its
    // width/left/top match the wordmark; the underscore only extends it downward.
    const lockup = intro.querySelector('.intro__lockup');

    const runFlip = () => {
      try {
        const target = document.querySelector('.site-header__brand-mark');
        if (!target || !stage || !lockup) throw new Error('intro: FLIP target missing');

        const src = lockup.getBoundingClientRect();
        const dst = target.getBoundingClientRect();
        if (!src.width || !dst.width) throw new Error('intro: zero-size rect');

        const scale = dst.width / src.width;
        const tx = dst.left - src.left;
        const ty = dst.top - src.top;

        // FLIP origin fix: the transform is applied to .intro__stage, but the
        // lockup sits offset inside it (flex-centered). Scaling around the
        // stage's own top-left would drag the lockup sideways. Pin the origin to
        // the lockup's top-left *relative to the stage* so translate(tx,ty) maps
        // the lockup exactly onto the header mark on both axes.
        const stageRect = stage.getBoundingClientRect();
        stage.style.transformOrigin = `${src.left - stageRect.left}px ${src.top - stageRect.top}px`;

        // Hero staggers in as the veil lifts — the handoff moment.
        fireReady();

        // Fade the dark veil to transparent so the page shows through.
        intro.style.transition = `background-color ${FADE_MS}ms ease`;
        intro.style.backgroundColor = 'transparent';

        // Fly the whole lockup onto the header logo AND dissolve it over the
        // last stretch, so the flown logo fades out exactly as it settles onto
        // the identical real header logo — no weird double-logo overlap.
        const DISSOLVE_MS = 260;
        stage.style.transition =
          `transform ${FLIP_MS}ms ${MOVE_EASE}, opacity ${DISSOLVE_MS}ms ease ${FLIP_MS - DISSOLVE_MS}ms`;
        stage.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
        stage.style.opacity = '0';

        // On finish, crossfade the flown logo out as the real header logo takes over.
        let cleared = false;
        const finish = () => { if (cleared) return; cleared = true; clearIntro(); };
        stage.addEventListener('transitionend', finish, { once: true });
        setTimeout(finish, FLIP_MS + 120); // fallback if transitionend is missed
      } catch (err) {
        // Fallback: never trap the user — fade the whole veil out and hand off.
        fireReady();
        clearIntro();
      }
    };

    setTimeout(runFlip, FLIP_START_MS);
  })();

  const STAGGER_MS = 110; // step between sibling reveals
  const STAGGER_CAP = 5;  // cap index so later items don't lag too long

  /* ---- 3) Scroll section reveals ---------------------------------------- */
  (() => {
    // Single-element reveal targets.
    const singles = Array.from(document.querySelectorAll('[data-reveal]'));

    // Stagger containers: their meaningful children become the reveal targets.
    const staggerHosts = Array.from(document.querySelectorAll('[data-reveal-stagger]'));
    const staggered = [];
    staggerHosts.forEach((host) => {
      // Only real content children — skip absolutely-positioned corner ticks.
      const kids = Array.from(host.children).filter(
        (el) => !el.classList.contains('tick-bl') && !el.classList.contains('tick-br')
      );
      kids.forEach((el, i) => {
        el.classList.add('reveal-child');
        el.style.transitionDelay = (Math.min(i, STAGGER_CAP) * STAGGER_MS) + 'ms';
        staggered.push(el);
      });
    });

    const targets = singles.concat(staggered);
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      // No observer support — just show everything.
      targets.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });

    targets.forEach((el) => io.observe(el));
  })();

  /* ---- 4) Image fade-in on load ----------------------------------------- */
  (() => {
    // Content images only. Exclude:
    //  - hero LCP slides (.hero__slide img)
    //  - the markets feature image (its own .is-swapping fade)
    const imgs = Array.from(document.querySelectorAll('picture img, .partner-tile__logo-wrap img'))
      .filter((img) => !img.closest('.hero__slide') && !img.hasAttribute('data-market-image'));

    imgs.forEach((img) => {
      img.classList.add('media-fade');
      const reveal = () => img.classList.add('is-loaded');
      if (img.complete && img.naturalWidth > 0) {
        // Cached / already decoded — reveal synchronously so it never stays hidden.
        reveal();
      } else {
        img.addEventListener('load', reveal, { once: true });
        img.addEventListener('error', reveal, { once: true }); // broken img must not stay invisible
      }
    });
  })();

  /* ---- 5) Quiet ambient loops off-screen -------------------------------- */
  (() => {
    const ambient = Array.from(document.querySelectorAll('[data-ambient]'));
    if (!ambient.length || !('IntersectionObserver' in window)) return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        // Pause CPU-burning keyframe loops while off-screen; resume on re-entry.
        entry.target.classList.toggle('is-paused', !entry.isIntersecting);
      });
    }, { rootMargin: '0px', threshold: 0 });

    ambient.forEach((el) => io.observe(el));
  })();
})();
